/* eslint-disable new-cap */
const sjcl = require('sjcl');
const forge = require('node-forge');
const { Binary } = require('binary');
const precond = require('precond');
const Algorithms = require('../ciphers/algorithms');
const cryptoUtils = require('./crypto-utils');
const { CorruptTdfPackage } = require('./errors');
const { EncryptionError } = require('./errors');
const utils = require('./utils');
const cryptoConfig = require('./crypto-config');
const errors = require('./errors');

let isSupported = false;

if (typeof window !== 'undefined') {
  sjcl.random.startCollectors();
  isSupported = true;
}

try {
  sjcl.beware["CBC mode is dangerous because it doesn't protect message integrity."]();
} catch (e) {
  /** ignore error * */
}

/**
 * Generate a random hex key
 * @return {String} - New key as a hex string
 */
function generateKey(length) {
  // Workaround when there isn't enough entropy (seen on IE 10)
  while (!sjcl.random.isReady()) {
    sjcl.random.addEntropy(Math.floor(Math.random() * Number.MAX_SAFE_INTEGER), 1, 'Math.random()');
  }

  return generateKeyInternal(length * 2 || 64);
}

/**
 * Generate a 32-byte initialization vector
 * @return {String}
 */
function generateInitializationVector(length) {
  return generateKeyInternal(length * 2 || 32);
}

/**
 * Generate a key/IV value
 * @param  {Number} length - Key length
 * @return {String}        - New key/IV
 */
function generateKeyInternal(length) {
  let key = '';
  do {
    const arr = longToByteArray(sjcl.random.randomWords(1));
    for (let i = 0; i < 4; i++) {
      let str = arr[i].toString(16);
      if (str.length === 1) {
        str = `0${str}`;
      }
      key += str;
    }
  } while (key.length < length);
  return key;
}

/**
 * Encrypt using a public key.
 * @param  {Binary} payload   - Payload to encrypt
 * @param  {String} publicKey - PEM formatted public key
 * @return {Promise<Binary>}  - Encrypted payload
 */
function encryptWithPublicKey(payload, publicKey) {
  precond.checkIsObject(payload);
  precond.checkIsString(publicKey);

  return new Promise(function encryptWithPublicKeyPromise(resolve, reject) {
    const key = forge.pki.publicKeyFromPem(publicKey);
    const ciphertext = key.encrypt(payload.asString(), 'RSA-OAEP');
    const result = Binary.fromBuffer(Buffer.from(ciphertext, 'binary'));
    return resolve(result);
  });
}

/**
 * Generate an RSA key pair
 * @param  {Number} [size] in bits
 * @return {Promise<Object>} Promise of:
 *   {
 *     publicKey: PEM,
 *     privateKey: PEM
 *   }
 */
function generateKeyPair(size) {
  const minKeySize = cryptoConfig.getMinAsymmetricKeySizeBits();

  if (!cryptoUtils.isValidAsymmetricKeySize(size, minKeySize)) {
    throw new Error('Invalid key size requested');
  }

  return new Promise(function promise(resolve, reject) {
    const { pki } = forge;
    const { rsa } = pki;
    const keySize = !size ? minKeySize : size;

    // TODO: Get async approach working after moving off of forge fork
    const keys = rsa.generateKeyPair({ bits: keySize, e: 0x10001 });
    return resolve({
      publicKey: pki.publicKeyToPem(keys.publicKey),
      privateKey: pki.privateKeyToPem(keys.privateKey),
    });
  });
}

/**
 * Create a byte array from a long value
 * @param  {Number} long - Long input
 * @return {Array}       - Byte array
 */
function longToByteArray(long) {
  // We want to represent the input as a 8-bytes array
  const byteArray = [0, 0, 0, 0];

  for (let index = 0; index < byteArray.length; index++) {
    /* eslint-disable no-bitwise */
    const byte = long & 0xff;
    /* eslint-enable no-bitwise */
    byteArray[index] = byte;
    long = (long - byte) / 256;
  }

  return byteArray;
}

function decryptSync(payload, key, iv, algorithm, authTag) {
  // Convert the key into a word array
  try {
    const cryptor = selectCryptor(algorithm);

    let payloadBytes = payload.asByteArray();
    if (cryptor.name === 'gcm') {
      payloadBytes = payloadBytes.concat(authTag.asByteArray());
    }

    const keyWordArray = sjcl.codec.bytes.toBits(key.asByteArray());
    const blockCipher = new sjcl.cipher.aes(keyWordArray);
    const decryptedBitArray = cryptor.decrypt(
      blockCipher,
      sjcl.codec.bytes.toBits(payloadBytes),
      sjcl.codec.bytes.toBits(iv.asByteArray())
    );
    const decryptedByteArray = sjcl.codec.bytes.fromBits(decryptedBitArray);
    const decryptedBinary = Binary.fromByteArray(decryptedByteArray);

    return { payload: decryptedBinary };
  } catch (err) {
    if (err.message === "gcm: tag doesn't match") {
      throw errors.IntegrityCompromisedError.create(err);
    }

    throw new CorruptTdfPackage('The TDF package is corrupt.', err);
  }
}

/**
 * Decrypt content
 * @param {Binary} payload - The payload to decrypt
 * @param {Binary} key     - The encryption key
 * @param {Binary} iv      - The initialization vector
 * @param {String} [algorithm] - The algorithm to use for encryption
 * @return {Object} - The decrypted output
 * {
 *   payload: {Binary} - The decrypted payload.
 *   [authTag]: {Binary} The authentication tag generated.
 * }
 * @property
 */
function decrypt(payload, key, iv, algorithm, authTag) {
  if (utils.isStream(payload)) {
    return cryptoUtils.readStream(payload).then(function dataHandler(data) {
      return Promise.resolve(decryptSync(data, key, iv, algorithm, authTag));
    });
  }

  return Promise.resolve(decryptSync(payload, key, iv, algorithm, authTag));
}

/**
 * Decrypt a public-key encrypted payload with a private key
 * @param  {Binary} encryptedPayload - Payload to decrypt
 * @param  {String} privateKey       - PEM formatted private key
 * @return {Promise<Binary>}         - Decrypted payload
 */
function decryptWithPrivateKey(encryptedPayload, privateKey) {
  precond.checkIsObject(encryptedPayload);
  precond.checkIsString(privateKey);

  return new Promise(function decryptWithPrivateKeyPromise(resolve, reject) {
    const key = forge.pki.privateKeyFromPem(privateKey);
    const cleartext = key.decrypt(encryptedPayload.asString(), 'RSA-OAEP');
    return resolve(Binary.fromString(cleartext));
  });
}

/**
 * Create a SHA256 hash
 * @param  {String}  content  String content
 * @return {Promise<String>} Hex hash
 */
function sha256(content) {
  const bitArray = sjcl.hash.sha256.hash(content);
  const hex = sjcl.codec.hex.fromBits(bitArray);
  return Promise.resolve(hex);
}

/**
 * Create an HMAC SHA256 hash
 * @param  {String} key     Key string
 * @param  {String} content Content string
 * @return {Promise<String>} Hex hash
 */
function hmac(key, content) {
  const hs256 = new sjcl.misc.hmac(sjcl.codec.hex.toBits(key), sjcl.hash.sha256);
  const hash = hs256.mac(sjcl.codec.utf8String.toBits(content));
  return Promise.resolve(sjcl.codec.hex.fromBits(hash));
}

/**
 * Encrypt content synchronously
 * @param  {Binary} payload - The payload to encrypt
 * @param  {Binary} key     - The encryption key
 * @param  {Binary} iv      - The initialization vector
 * @return {Binary}         - The encrypted output
 */
function encryptSync(payload, key, iv, algorithm) {
  // Convert the key into a word array
  try {
    const encryptor = selectCryptor(algorithm);

    const keyWordArray = sjcl.codec.bytes.toBits(key.asByteArray());
    const blockCipher = new sjcl.cipher.aes(keyWordArray);
    const encryptResult = encryptor.encrypt(
      blockCipher,
      sjcl.codec.bytes.toBits(payload.asByteArray()),
      sjcl.codec.bytes.toBits(iv.asByteArray())
    );

    payload = sjcl.codec.bytes.fromBits(encryptResult);

    let authTag;
    if (encryptor.name === 'gcm') {
      authTag = payload.slice(-16);
      payload = payload.slice(0, -16);
    }

    return {
      payload: Binary.fromByteArray(payload),
      authTag: Binary.fromByteArray(authTag),
    };
  } catch (err) {
    throw new EncryptionError(`Encryption failed.  ${err.message}`, err.message);
  }
}

function selectCryptor(algorithm) {
  if (algorithm === Algorithms.AES_256_GCM) {
    return sjcl.mode.gcm;
  }
  return sjcl.mode.cbc;
}

/**
 * Encrypt content.
 * @param {Binary} payload - The payload to encrypt
 * @param {Binary} key     - The encryption key
 * @param {Binary} iv      - The initialization vector
 * @param {String} [algorithm] - The algorithm to use for encryption
 * @return {Object} - The encrypted output
 * {
 *   payload: {Binary} - The encrypted payload.
 *   [authTag]: {Binary} The authentication tag generated.
 * }
 * @property
 */
function encrypt(payload, key, iv, algorithm) {
  return Promise.resolve(encryptSync(payload, key, iv, algorithm));
}

module.exports = {
  decrypt,
  encrypt,
  decryptWithPrivateKey,
  encryptWithPublicKey,
  generateKey,
  generateKeyPair,
  generateInitializationVector,
  isSupported,
  method: 'http://www.w3.org/2001/04/xmlenc#aes256-cbc',
  sha256,
  hmac,
  name: 'BrowserJsCryptoService',
};
